import React, { useState, useEffect, useRef } from "react";
import {
  Box,
  Typography,
  Grid,
  Paper,
  Slider,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Tooltip,
  IconButton,
  FormControlLabel,
  Switch,
} from "@mui/material";
import { Info as InfoIcon } from "@mui/icons-material";
import {
  calculateAge,
  getExactLifeExpectancy,
} from "../../../../../../utils/dateUtils";

import logger from '../../../../../../utils/logger';

const marks = [
  { value: -5, label: "-5" },
  { value: -2.5, label: "-2.5" },
  { value: 0, label: "0" },
  { value: 2.5, label: "+2.5" },
  { value: 5, label: "+5" },
];

const BasicAssessmentStep = ({ data, member, onUpdate }) => {
  const [formData, setFormData] = useState({
    baseAge: 0,
    familyHistoryImpact: 0,
    lifestyleImpact: 0,
    healthStatusImpact: 0,
    calculatedLifeExpectancy: 0,
    healthScore: 50,
    isOverridden: false,
    userOverriddenLifeExpectancy: 0,
    isValid: false,
    assessment_date: null,
  });

  const baseLifeExpectancyRef = useRef(0);
  const [isLoading, setIsLoading] = useState(false);
  const initializedRef = useRef(false);

  const calculateHealthScore = (familyHistory, lifestyle, healthStatus) => {
    // Base score is 50
    const baseScore = 50;

    // Weight the impacts (as per the outline)
    // Family History: 35% weight
    // Lifestyle: 40% weight
    // Health Status: 25% weight
    const weightedFamilyHistory = familyHistory * 0.35;
    const weightedLifestyle = lifestyle * 0.4;
    const weightedHealthStatus = healthStatus * 0.25;

    // Calculate total impact (each slider is -5 to +5)
    const totalImpact =
      weightedFamilyHistory + weightedLifestyle + weightedHealthStatus;

    // Calculate final score (ensure it stays within 0-100 range)
    const finalScore = Math.min(Math.max(baseScore + totalImpact, 0), 100);

    return Math.round(finalScore);
  };

  useEffect(() => {
    const initializeData = async () => {
      logger.info(
        "BasicAssessmentStep - Received data:",
        JSON.stringify(data, null, 2),
      );
      logger.info(
        "BasicAssessmentStep - Member:",
        JSON.stringify(member, null, 2),
      );

      if (!member?.dateOfBirth || !member?.gender) {
        logger.info("Missing required member data");
        return;
      }

      setIsLoading(true);

      try {
        const currentAge = calculateAge(member.dateOfBirth);
        if (currentAge > 0) {
          const lifeExpectancy = await getExactLifeExpectancy(
            currentAge,
            member.gender,
          );
          baseLifeExpectancyRef.current = lifeExpectancy;

          // Only initialize if we haven't done so before
          if (!initializedRef.current) {
            // Use member's basicAssessment data if available
            const basicAssessment = member?.basicAssessment;
            const newFormData = {
              baseAge: Number(currentAge),
              familyHistoryImpact: basicAssessment?.family_history_impact || 0,
              lifestyleImpact: basicAssessment?.lifestyle_impact || 0,
              healthStatusImpact: basicAssessment?.health_status_impact || 0,
              calculatedLifeExpectancy:
                basicAssessment?.calculated_life_expectancy
                  ? parseFloat(basicAssessment.calculated_life_expectancy)
                  : lifeExpectancy,
              healthScore: basicAssessment?.calculated_health_score || 50,
              isOverridden: Boolean(basicAssessment?.manual_life_expectancy),
              userOverriddenLifeExpectancy:
                basicAssessment?.manual_life_expectancy
                  ? parseFloat(basicAssessment.manual_life_expectancy)
                  : 0,
              isValid: true,
              assessment_date:
                basicAssessment?.assessment_date || new Date().toISOString(),
            };

            logger.info(
              "BasicAssessmentStep - Initializing with data:",
              JSON.stringify(newFormData, null, 2),
            );
            setFormData(newFormData);
            // Notify parent of initial valid state
            onUpdate(newFormData);
            initializedRef.current = true;
          }
        }
      } catch (error) {
        logger.error("Error initializing basic assessment:", error);
      } finally {
        setIsLoading(false);
      }
    };

    initializeData();
  }, [member?.dateOfBirth, member?.gender, member?.basicAssessment]);

  // Separate effect to handle data updates
  useEffect(() => {
    if (initializedRef.current && data && !isLoading) {
      const newFormData = {
        ...formData,
        familyHistoryImpact: data.familyHistoryImpact,
        lifestyleImpact: data.lifestyleImpact,
        healthStatusImpact: data.healthStatusImpact,
        calculatedLifeExpectancy: data.calculatedLifeExpectancy,
        healthScore: data.healthScore,
        isOverridden: data.isOverridden,
        userOverriddenLifeExpectancy: data.userOverriddenLifeExpectancy,
        assessment_date: data.assessment_date,
        isValid: true,
      };

      setFormData(newFormData);
    }
  }, [data, isLoading]);

  // Handle slider changes
  const handleSliderChange = (field) => (event, newValue) => {
    // Ensure newValue is a number
    const numericValue = parseFloat(newValue);
    if (isNaN(numericValue)) return;

    setFormData((prevData) => {
      const newFormData = {
        ...prevData,
        [field]: numericValue,
      };

      // Calculate new health score based on all slider values
      const healthScore = calculateHealthScore(
        field === "familyHistoryImpact"
          ? numericValue
          : prevData.familyHistoryImpact,
        field === "lifestyleImpact" ? numericValue : prevData.lifestyleImpact,
        field === "healthStatusImpact"
          ? numericValue
          : prevData.healthStatusImpact,
      );

      // Calculate new life expectancy with weighted adjustments
      const familyHistoryAdjustment = newFormData.familyHistoryImpact * 1.05;
      const lifestyleAdjustment = newFormData.lifestyleImpact * 1.2;
      const healthStatusAdjustment = newFormData.healthStatusImpact * 0.75;

      const totalAdjustment =
        familyHistoryAdjustment + lifestyleAdjustment + healthStatusAdjustment;
      const adjustedLifeExpectancy =
        baseLifeExpectancyRef.current + totalAdjustment;

      // Update form data with new calculations
      const updatedFormData = {
        ...newFormData,
        calculatedLifeExpectancy: parseFloat(adjustedLifeExpectancy.toFixed(2)),
        healthScore: healthScore,
        isValid: true,
        assessment_date: new Date().toISOString(),
      };

      // Update parent with new data
      onUpdate(updatedFormData);

      return updatedFormData;
    });
  };

  const handleOverrideChange = (event) => {
    const isOverridden = event.target.checked;
    setFormData((prevData) => {
      const newFormData = {
        ...prevData,
        isOverridden,
        userOverriddenLifeExpectancy: isOverridden
          ? Math.ceil(baseLifeExpectancyRef.current)
          : 0,
        calculatedLifeExpectancy: isOverridden
          ? Math.ceil(baseLifeExpectancyRef.current)
          : baseLifeExpectancyRef.current +
            prevData.familyHistoryImpact * 1.05 +
            prevData.lifestyleImpact * 1.2 +
            prevData.healthStatusImpact * 0.75,
      };

      onUpdate(newFormData);
      return newFormData;
    });
  };

  const handleManualLifeExpectancyChange = (event) => {
    const value = parseFloat(event.target.value);
    setFormData((prevData) => {
      const newFormData = {
        ...prevData,
        userOverriddenLifeExpectancy: value,
        calculatedLifeExpectancy: value,
      };

      onUpdate(newFormData);
      return newFormData;
    });
  };

  // Generate age options for dropdown
  const generateAgeOptions = () => {
    const options = [];
    const currentAge = Math.floor(formData.baseAge);
    for (let age = currentAge; age <= 120; age++) {
      options.push(
        <MenuItem key={age} value={age}>
          {age} years
        </MenuItem>,
      );
    }
    return options;
  };

  // Helper function to get color based on value
  const getValueColor = (value) => {
    if (value > 0) return "success.main";
    if (value < 0) return "error.main";
    return "text.secondary";
  };

  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        Basic Health Assessment
      </Typography>

      <Paper sx={{ p: 3, mb: 3, bgcolor: "background.paper" }}>
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Box sx={{ mb: 4 }}>
              <Typography variant="subtitle1" gutterBottom fontWeight="bold">
                Client Details
              </Typography>
              <Typography variant="body1">
                <strong>Name:</strong> {member?.name || "N/A"}
              </Typography>
              <Typography variant="body1">
                <strong>Age:</strong>{" "}
                {formData.baseAge ? formData.baseAge.toFixed(2) : "N/A"} years
              </Typography>
              <Typography variant="body1">
                <strong>Gender:</strong>{" "}
                {member?.gender
                  ? member.gender.charAt(0).toUpperCase() +
                    member.gender.slice(1)
                  : "N/A"}
              </Typography>
              <Typography variant="body1">
                <strong>Base Life Expectancy:</strong>{" "}
                {baseLifeExpectancyRef.current.toFixed(2)} years
              </Typography>
            </Box>
          </Grid>

          <Grid item xs={12} md={6}>
            <Box
              sx={{
                p: 2,
                bgcolor: "primary.light",
                color: "primary.contrastText",
                borderRadius: 1,
                height: "100%",
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
                alignItems: "center",
              }}
            >
              <Typography variant="h5" gutterBottom>
                Health Score
              </Typography>
              <Typography variant="h3" sx={{ my: 1 }}>
                {formData.healthScore}/100
              </Typography>
              <Typography variant="h6">
                Life Expectancy: {formData.calculatedLifeExpectancy.toFixed(2)}{" "}
                years
              </Typography>
            </Box>
          </Grid>
        </Grid>
      </Paper>

      <Paper sx={{ p: 3, mb: 3 }}>
        <Box sx={{ mb: 3 }}>
          <FormControlLabel
            control={
              <Switch
                checked={formData.isOverridden}
                onChange={handleOverrideChange}
                color="primary"
              />
            }
            label="Manual Override"
          />
        </Box>

        {formData.isOverridden ? (
          <Box sx={{ mb: 3 }}>
            <FormControl fullWidth>
              <InputLabel>Select Life Expectancy</InputLabel>
              <Select
                value={formData.userOverriddenLifeExpectancy}
                onChange={handleManualLifeExpectancyChange}
                label="Select Life Expectancy"
              >
                {generateAgeOptions()}
              </Select>
            </FormControl>
          </Box>
        ) : (
          <>
            <Box sx={{ mb: 3 }}>
              <Typography
                variant="subtitle1"
                gutterBottom
                fontWeight="bold"
                sx={{ display: "flex", alignItems: "center" }}
              >
                Family History Impact
                <Tooltip title="Adjust based on family longevity and health history">
                  <IconButton size="small">
                    <InfoIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Typography>
              <Slider
                value={formData.familyHistoryImpact}
                onChange={handleSliderChange("familyHistoryImpact")}
                step={0.5}
                marks={marks}
                min={-5}
                max={5}
                valueLabelDisplay="auto"
                sx={{
                  "& .MuiSlider-thumb": {
                    color: getValueColor(formData.familyHistoryImpact),
                  },
                  "& .MuiSlider-track": {
                    color: getValueColor(formData.familyHistoryImpact),
                  },
                }}
              />
              <Typography
                variant="body2"
                color={getValueColor(formData.familyHistoryImpact)}
              >
                {formData.familyHistoryImpact > 0
                  ? `+${formData.familyHistoryImpact}`
                  : formData.familyHistoryImpact}{" "}
                years
              </Typography>
            </Box>

            <Box sx={{ mb: 3 }}>
              <Typography
                variant="subtitle1"
                gutterBottom
                fontWeight="bold"
                sx={{ display: "flex", alignItems: "center" }}
              >
                Lifestyle Impact
                <Tooltip title="Adjust based on diet, exercise, and other lifestyle factors">
                  <IconButton size="small">
                    <InfoIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Typography>
              <Slider
                value={formData.lifestyleImpact}
                onChange={handleSliderChange("lifestyleImpact")}
                step={0.5}
                marks={marks}
                min={-5}
                max={5}
                valueLabelDisplay="auto"
                sx={{
                  "& .MuiSlider-thumb": {
                    color: getValueColor(formData.lifestyleImpact),
                  },
                  "& .MuiSlider-track": {
                    color: getValueColor(formData.lifestyleImpact),
                  },
                }}
              />
              <Typography
                variant="body2"
                color={getValueColor(formData.lifestyleImpact)}
              >
                {formData.lifestyleImpact > 0
                  ? `+${formData.lifestyleImpact}`
                  : formData.lifestyleImpact}{" "}
                years
              </Typography>
            </Box>

            <Box sx={{ mb: 3 }}>
              <Typography
                variant="subtitle1"
                gutterBottom
                fontWeight="bold"
                sx={{ display: "flex", alignItems: "center" }}
              >
                Health Status Impact
                <Tooltip title="Adjust based on current health conditions and medical history">
                  <IconButton size="small">
                    <InfoIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Typography>
              <Slider
                value={formData.healthStatusImpact}
                onChange={handleSliderChange("healthStatusImpact")}
                step={0.5}
                marks={marks}
                min={-5}
                max={5}
                valueLabelDisplay="auto"
                sx={{
                  "& .MuiSlider-thumb": {
                    color: getValueColor(formData.healthStatusImpact),
                  },
                  "& .MuiSlider-track": {
                    color: getValueColor(formData.healthStatusImpact),
                  },
                }}
              />
              <Typography
                variant="body2"
                color={getValueColor(formData.healthStatusImpact)}
              >
                {formData.healthStatusImpact > 0
                  ? `+${formData.healthStatusImpact}`
                  : formData.healthStatusImpact}{" "}
                years
              </Typography>
            </Box>
          </>
        )}
      </Paper>
    </Box>
  );
};

export default BasicAssessmentStep;
